import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BOARD_COLUMNS,
    type BodyMode,
    type CardView,
} from '../board/columnConfig';
import {
    columnDefaults,
    columnFilterKeys,
    defaultFilters,
    sortOptions,
    useSchema,
} from '../api/useSchema';
import {
    carryFilters,
    gatedNamer,
    resolveCategory,
    resolveSlotCategory,
    seedCategory,
    type CategoryConfig,
} from '../board/categories';
import { useBoardGroupsValue } from './useBoardGroups';
import { useBoardRefresh } from '../api/useBoardTick';
import {
    buildColumnShareUrl,
    clearBoardParams,
    hasSharedColumns,
    readSharedColumns,
    readSharedSources,
    showAllFilters,
    type SharedColumns,
} from './boardShareLink';
import { applyPreference, usePreferences } from './usePreferences';
import type { BoardFilters, ColumnId } from '../api/boardTypes';
import { useProfileBar, type ColumnLayout } from './useProfileBar';
import { useAccessFeatures } from '../../../../auth/useAccessFeatures';

const NOW_TICK_MS = 15_000;

export function useBoardState() {
    useBoardRefresh();

    // Drives the freshness label between polls; the data itself only moves on
    // the minute tick.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), NOW_TICK_MS);
        return () => clearInterval(timer);
    }, []);

    const { data: schema, isLoading, error } = useSchema();
    // Read once here and provided to the tree: the picker, the column headers,
    // the info panel and the cards all name a group from the same resolution.
    const boardGroups = useBoardGroupsValue(schema);
    const { isPending: accessPending } = useAccessFeatures();
    const offered = boardGroups.offered;
    const {
        preferences,
        isLoaded: prefsLoaded,
        saveFilters,
        saveLayout,
        saveSource,
        activeProfile,
        setActiveProfile,
        profileOrder,
        setProfileOrder,
    } = usePreferences();

    // Read before the first paint and hold it: the parameters get stripped from
    // the address bar as soon as they are applied.
    const [shared] = useState<SharedColumns>(() =>
        readSharedColumns(window.location.search)
    );
    const [sharedSources] = useState<Partial<Record<ColumnId, ColumnId>>>(() =>
        readSharedSources(window.location.search)
    );

    const [filters, setFilters] = useState<Record<string, BoardFilters> | null>(
        null
    );
    const [layout, setLayout] = useState<Record<string, ColumnLayout> | null>(
        null
    );
    const [sources, setSources] = useState<Record<string, string> | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [toast, setToast] = useState<string | null>(null);

    useEffect(() => {
        if (!schema || !prefsLoaded || accessPending || filters !== null) return;

        const nextFilters: Record<string, BoardFilters> = {};
        const nextLayout: Record<string, ColumnLayout> = {};
        const nextSources: Record<string, string> = {};

        for (const config of BOARD_COLUMNS) {
            const stored = preferences.columns[config.id];
            const incoming = shared[config.id as ColumnId];
            // Resolve before seeding: a category the schema no longer lists must fall
            // back here, or the filters get shaped for a column the pane will not query.
            const category = resolveSlotCategory(schema, config.id, {
                linkSource: sharedSources[config.id as ColumnId],
                linkGroup: incoming?.group,
                storedSource: stored?.source,
                storedGroup: stored?.group,
            }, gatedNamer(offered));
            const seed = defaultFilters(schema, category.source);
            const base = applyPreference(seed, stored);
            // The link's values merge first so the corrections below outrank them.
            const merged = { ...base, ...(incoming ?? {}) };
            // A sort key belongs to the category it was written for; on the fallback
            // path it would reach the wire as a key the seed cannot send.
            const sortKeys = sortOptions(schema, category.source);
            nextFilters[config.id] = {
                ...merged,
                // The category is the pane's identity; a link cannot override it
                // after resolution has already accounted for what the link asked.
                group: category.group,
                sortBy: sortKeys.includes(merged.sortBy) ? merged.sortBy : seed.sortBy,
            };
            nextLayout[config.id] = {
                body: (stored?.body ?? config.defaultBody) as BodyMode,
                view: (stored?.view ?? config.defaultView) as CardView,
            };
            nextSources[config.id] = category.source;
        }

        setFilters(nextFilters);
        setLayout(nextLayout);
        setSources(nextSources);

        if (hasSharedColumns(shared)) {
            setDialogOpen(true);
            clearBoardParams();
        }
    }, [schema, prefsLoaded, accessPending, offered, preferences, shared, sharedSources, filters]);

    // A stored category the schema no longer lists degrades to the slot's seed.
    const configs = useMemo(() => {
        if (!sources || !filters) return [];
        return BOARD_COLUMNS.map(seed => {
            const source = sources[seed.id] ?? seed.source;
            const stored = filters[seed.id];
            // Same precedence as resolveSlotCategory: the stored group, then the
            // backend's default for the column. The seed no longer names one.
            const group =
                stored?.group !== undefined
                    ? stored.group
                    : columnDefaults(schema, source).group;
            const category =
                resolveCategory(schema, source, group, boardGroups) ??
                seedCategory(seed.id, schema);
            return { ...category, id: seed.id };
        });
    }, [schema, sources, filters, boardGroups]);

    // One authority for the pane's backend column: the resolved category, not the
    // stored source, which can name a category the schema no longer lists.
    // Null until the board has resolved, so it can stand in for `sources`
    // everywhere a resolved column is wanted rather than a stored one.
    const sourceBySlot = useMemo(() => {
        if (configs.length === 0) return null;
        const out: Record<string, ColumnId> = {};
        for (const config of configs) out[config.id] = config.source;
        return out;
    }, [configs]);

    // Named for the slot, not the schema helper it calls: the two would shadow.
    const defaultsBySlot = useMemo(() => {
        const out: Record<string, BoardFilters> = {};
        for (const config of configs) {
            out[config.id] = defaultFilters(schema, config.source);
        }
        return out;
    }, [schema, configs]);

    const handleFiltersChange = useCallback(
        (column: string, patch: Partial<BoardFilters>) => {
            setFilters(prev => {
                if (!prev) return prev;
                const next = { ...prev[column], ...patch };
                saveFilters(column, next);
                return { ...prev, [column]: next };
            });
        },
        [saveFilters]
    );

    // Show-all is per column, driven by what the column declares in
    // filter_spec: a threshold the column cannot send must not appear here
    // either, or reset puts a parameter on the wire that answers 400.
    const handleReset = useCallback(
        (column: string) => {
            // Keyed on the pane's category, not its slot: a slot holding Active must
            // widen the move thresholds, not the conviction ones it cannot send.
            const source = sourceBySlot?.[column] ?? column;
            const widest = showAllFilters(columnFilterKeys(schema, source));
            setFilters(prev => {
                if (!prev) return prev;
                const next = { ...prev[column], ...widest };
                saveFilters(column, next);
                return { ...prev, [column]: next };
            });
        },
        [saveFilters, schema, sourceBySlot]
    );

    const handleLayoutChange = useCallback(
        (column: string, patch: Partial<ColumnLayout>) => {
            setLayout(prev =>
                prev
                    ? { ...prev, [column]: { ...prev[column], ...patch } }
                    : prev
            );
            saveLayout(column, patch);
        },
        [saveLayout]
    );

    const handleCategoryChange = useCallback(
        (slot: string, category: CategoryConfig) => {
            const seed = defaultFilters(schema, category.source);
            setFilters(prev => {
                if (!prev) return prev;
                const next = carryFilters(
                    { ...seed, group: category.group },
                    prev[slot],
                    sortOptions(schema, category.source),
                );
                saveFilters(slot, next);
                return { ...prev, [slot]: next };
            });
            setSources(prev => (prev ? { ...prev, [slot]: category.source } : prev));
            saveSource(slot, category.source);
        },
        [schema, saveFilters, saveSource],
    );

    const profileBar = useProfileBar({
        schema,
        filters,
        layout,
        // The resolved column, so a pane that fell back is not read as dirty
        // against a column the reader cannot see.
        sources: sourceBySlot,
        offered,
        setFilters,
        setLayout,
        setSources,
        saveFilters,
        saveLayout,
        saveSource,
        activeProfile,
        setActiveProfile,
        profileOrder,
        setProfileOrder,
    });

    const handleShare = useCallback(
        (column: string) => {
            if (!filters || !sourceBySlot) return '';
            return buildColumnShareUrl(
                column as ColumnId,
                filters[column],
                sourceBySlot[column]
            );
        },
        [filters, sourceBySlot]
    );

    // The category rides with the filters: saving one without the other would put
    // the pane back on its old category with the link's filters on the next load.
    const handleSaveShared = useCallback(() => {
        setDialogOpen(false);
        if (!filters || !sourceBySlot) return;
        for (const id of Object.keys(shared)) {
            saveFilters(id, filters[id]);
            saveSource(id, sourceBySlot[id]);
        }
        setToast('Filters saved to your board.');
    }, [filters, sourceBySlot, shared, saveFilters, saveSource]);

    // Named for the category the link carries, not the one the slot happens to hold.
    const sharedForDialog = useMemo(
        () =>
            BOARD_COLUMNS.filter(c => shared[c.id as ColumnId]).map(c => {
                const incoming = shared[c.id as ColumnId] as Partial<BoardFilters>;
                // The same chain as the mount effect, stored rung included: the
                // dialog must name the category the reader is about to get.
                const category = resolveSlotCategory(
                    schema,
                    c.id,
                    {
                        linkSource: sharedSources[c.id as ColumnId],
                        linkGroup: incoming.group,
                        storedSource: preferences.columns[c.id]?.source,
                        storedGroup: preferences.columns[c.id]?.group,
                    },
                    boardGroups,
                );
                return { id: c.id as ColumnId, title: category.title, filters: incoming };
            }),
        [shared, sharedSources, schema, preferences, boardGroups]
    );

    const ready =
        !isLoading &&
        Boolean(schema) &&
        prefsLoaded &&
        filters !== null &&
        layout !== null &&
        sources !== null;

    return {
        now,
        schema,
        error,
        ready,
        boardGroups,
        configs,
        filters,
        layout,
        defaultsBySlot,
        handleFiltersChange,
        handleReset,
        handleLayoutChange,
        handleCategoryChange,
        handleShare,
        profileBar,
        dialogOpen,
        setDialogOpen,
        sharedForDialog,
        handleSaveShared,
        toast,
        setToast,
    };
}
